import { connect } from 'react-redux';
import { push } from 'redux-first-history';
import queryString from 'query-string';

import EditPaymentLengthPage from './component';
import { getIreneDashboard, stopPayments } from '@rental-manager/actions/requests';
import {
  DEFAULT_ERROR,
  PAYMENT_LENGTH_UPDATED,
  STOP_PAYMENTS_SUCCESS,
  SWITCH_TO_M2M,
} from '@rental-manager/constants/NotificationConstants';
import { closeNotification, showModalNotification, showPageNotification } from '@common/actions/NotificationActions';
import { trackValidationError } from '@common/actions/TrackingActions';
import { checkRequestActionSuccess, getSignInPage } from '@common/paymentsUtils/api';
import { getPaymentErrorInfo } from '../../../../utils/getPaymentErrorInfo';
import paths from '@rental-manager/pathsManifest';
import HTTP_STATUS from '@common/constants/HttpConstants';
import type { ErrorInterface } from '@common/types';
import { logError } from '@zg-rentals/log-error';
import type { UpdateRentTermsWebApiRequestDto } from '@zg-rentals/ts-rent-payment-client';
import { UpdateRentTermsWebApiHandler_updateRentTerms } from '@zg-rentals/ts-rent-payment-client';

const mapStateToProps = (state, ownProps) => {
  const rentTermsKeyFromQueryString = queryString.parse(ownProps.location.search).rentTermsKey;
  const { currentTerm, nextTerm, listingAlias, selectedLease } = state.payments.landlord.property;

  let rentTerm;
  if (rentTermsKeyFromQueryString && nextTerm?.rentTermsKey === rentTermsKeyFromQueryString) {
    rentTerm = nextTerm;
  } else if (rentTermsKeyFromQueryString && currentTerm?.rentTermsKey === rentTermsKeyFromQueryString) {
    rentTerm = currentTerm;
  }

  const { rentalProtectionPolicy } = rentTerm;
  const { paymentsEncodedLeaseId } = selectedLease || {};

  return {
    ...rentTerm,
    rentalProtectionPolicyStatus: rentalProtectionPolicy && rentalProtectionPolicy.policyStatus,
    listingAlias,
    paymentsEncodedLeaseId,
  };
};

export const mapDispatchToProps = (dispatch, { baseUrl, propertyId }) => ({
  requestEditPaymentLength: async (
    { endDate, numberOfPayments, monthToMonth, rentTermsKey },
    paymentsEncodedLeaseId,
  ) => {
    try {
      await dispatch(closeNotification());
      const updateRentTermsPayload: UpdateRentTermsWebApiRequestDto = {
        resourceId: propertyId,
        rentTermsKey,
        numberOfPayments,
        monthToMonth,
        paymentsEncodedLeaseId,
      };
      await UpdateRentTermsWebApiHandler_updateRentTerms(updateRentTermsPayload);
      const [landlordStatusResponse] = await dispatch(getIreneDashboard({ propertyId }));
      await checkRequestActionSuccess(landlordStatusResponse);
      await dispatch(push(baseUrl));
      await (numberOfPayments
        ? dispatch(showPageNotification(PAYMENT_LENGTH_UPDATED, { paymentEndDate: endDate }))
        : dispatch(showPageNotification(SWITCH_TO_M2M)));

      return { error: false };
    } catch (err) {
      const error = err as ErrorInterface;
      if (error.action && error.action.payload) {
        const { status } = error.action.payload;

        // If 401 status, redirect to sign-in
        if (status === HTTP_STATUS.SC_UNAUTHORIZED) {
          window.location = getSignInPage();
          return null;
        }
      }

      logError({ error: err });
      dispatch(showModalNotification(DEFAULT_ERROR));
      return { error: true };
    }
  },
  requestStopPayments: async (rentTermsKey) => {
    try {
      await dispatch(closeNotification());
      await dispatch(stopPayments({ propertyId, rentTermsKey }));
      await dispatch(push(baseUrl + paths.dashboard.stopPaymentsFeedbackPage));
      await dispatch(showModalNotification(STOP_PAYMENTS_SUCCESS));
      return { error: false };
    } catch (err) {
      const { httpStatus } = getPaymentErrorInfo(err);
      if (httpStatus === HTTP_STATUS.SC_UNAUTHORIZED) {
        window.location = getSignInPage();
        return null;
      }
      logError({ error: err });
      dispatch(showModalNotification(DEFAULT_ERROR));
      return { error: true };
    }
  },
  onClose: async () => {
    await dispatch(closeNotification());
    await dispatch(push(baseUrl));
  },
  closeNotification: () => dispatch(closeNotification()),
  trackValidationError: (data) => dispatch(trackValidationError(data)),
});

const mergeProps = (stateProps, dispatchProps) => ({
  ...stateProps,
  ...dispatchProps,
  requestEditPaymentLength: (payload) =>
    dispatchProps.requestEditPaymentLength(payload, stateProps.paymentsEncodedLeaseId),
});

export default connect(mapStateToProps, mapDispatchToProps, mergeProps)(EditPaymentLengthPage);
